/**
 * Autorska checklista edukacyjna dotycząca sygnałów wypalenia zawodowego.
 * 20 pytań w czterech obszarach, skala 0–4 punkty (0–80 łącznie).
 * Narzędzie nie jest kwestionariuszem diagnostycznym.
 */

export type BurnoutArea = {
  id: "energia" | "dystans" | "koncentracja" | "emocje";
  name: string;
  shortName: string;
};

export const burnoutAreas: BurnoutArea[] = [
  { id: "energia", name: "Energia i wyczerpanie", shortName: "Energia" },
  { id: "dystans", name: "Dystans i stosunek do pracy", shortName: "Stosunek do pracy" },
  { id: "koncentracja", name: "Koncentracja i funkcjonowanie poznawcze", shortName: "Koncentracja" },
  { id: "emocje", name: "Emocje i codzienne funkcjonowanie", shortName: "Emocje i funkcjonowanie" },
];

export const burnoutScale = [
  { label: "Nigdy", value: 0 },
  { label: "Rzadko", value: 1 },
  { label: "Czasami", value: 2 },
  { label: "Często", value: 3 },
  { label: "Prawie zawsze", value: 4 },
] as const;

export type BurnoutQuestion = { id: number; area: BurnoutArea["id"]; text: string };

export const burnoutQuestions: BurnoutQuestion[] = [
  { id: 1, area: "energia", text: "Budzę się zmęczona lub zmęczony, nawet po przespanej nocy." },
  { id: 2, area: "energia", text: "Po pracy zostaje mi tak mało energii, że odkładam sprawy prywatne na później." },
  { id: 3, area: "energia", text: "Zwykłe zadania kosztują mnie ostatnio więcej wysiłku niż kiedyś." },
  { id: 4, area: "energia", text: "Weekend lub kilka dni wolnego nie wystarcza, żeby poczuć regenerację." },
  { id: 5, area: "energia", text: "Odczuwam napięcie w ciele: bóle głowy, karku, żołądka lub problemy ze snem." },

  { id: 6, area: "dystans", text: "Łapię się na tym, że myślę o pracy z niechęcią, zanim jeszcze ją zacznę." },
  { id: 7, area: "dystans", text: "Reaguję bardziej cynicznie lub obojętnie na sprawy, które kiedyś mnie angażowały." },
  { id: 8, area: "dystans", text: "Trzymam ludzi na dystans, bo nie mam w sobie miejsca na kolejne rozmowy." },
  { id: 9, area: "dystans", text: "Mam wrażenie, że moja praca ma mniejszy sens niż wcześniej." },
  { id: 10, area: "dystans", text: "Robię to, co konieczne, ale bez wcześniejszego zaangażowania." },

  { id: 11, area: "koncentracja", text: "Trudno mi utrzymać uwagę na jednym zadaniu dłużej niż kilkanaście minut." },
  { id: 12, area: "koncentracja", text: "Częściej niż kiedyś zapominam o ustaleniach, terminach lub szczegółach." },
  { id: 13, area: "koncentracja", text: "Odkładam decyzje, które wcześniej podejmowałabym lub podejmowałbym szybciej." },
  { id: 14, area: "koncentracja", text: "Popełniam drobne błędy, które wcześniej mi się nie zdarzały." },
  { id: 15, area: "koncentracja", text: "Czytam lub słucham czegoś i orientuję się, że nie zarejestrowałam lub nie zarejestrowałem treści." },

  { id: 16, area: "emocje", text: "Reaguję nieproporcjonalnie mocno na drobne sytuacje: irytacją, złością lub łzami." },
  { id: 17, area: "emocje", text: "Mam poczucie, że nie robię wystarczająco dużo, mimo że pracuję dużo." },
  { id: 18, area: "emocje", text: "Ograniczam kontakty z bliskimi, bo brakuje mi na nie siły." },
  { id: 19, area: "emocje", text: "Trudno mi cieszyć się rzeczami, które wcześniej sprawiały mi przyjemność." },
  { id: 20, area: "emocje", text: "Myślę o tym, żeby zrobić dłuższą przerwę lub odejść, bo dłużej tak nie da rady." },
];

export type BurnoutRange = {
  min: number;
  max: number;
  headline: string;
  body: string[];
};

export const burnoutRanges: BurnoutRange[] = [
  {
    min: 0,
    max: 20,
    headline: "Na ten moment widzę u Ciebie niewiele sygnałów wypalenia",
    body: [
      "Twoje odpowiedzi wskazują na pojedyncze momenty zmęczenia, a nie na utrwalony wzorzec przeciążenia. To dobry moment, żeby zadbać o to, co dzisiaj działa: sen, przerwy, granice między pracą a resztą życia.",
      "Warto wracać do takiej checklisty co jakiś czas, zwłaszcza w okresach większego obciążenia. Obraz potrafi zmienić się szybciej, niż się spodziewamy.",
    ],
  },
  {
    min: 21,
    max: 40,
    headline: "Twoja praca zaczyna kosztować Cię więcej energii",
    body: [
      "Widać sygnały, które zwykle pojawiają się wcześniej niż samo wypalenie: dłuższy czas regeneracji, mniejsza cierpliwość, trudniejsze wchodzenie w zadania. Nie musi to oznaczać nic poważnego, ale zwykle warto potraktować to jako informację zwrotną.",
      "Na tym etapie często pomagają zmiany, które brzmią mało spektakularnie: realne przerwy w ciągu dnia, ograniczenie liczby równoległych spraw, rozmowa o zakresie odpowiedzialności i oddanie części zadań innym.",
    ],
  },
  {
    min: 41,
    max: 60,
    headline: "Widać wiele sygnałów, których nie warto ignorować",
    body: [
      "Objawy związane z wypaleniem pojawiają się u Ciebie w kilku obszarach jednocześnie. Taki wzorzec rzadko cofa się sam po jednym urlopie, ponieważ zwykle wynika z długo utrzymującego się obciążenia, a nie z chwilowego zmęczenia.",
      "To moment, w którym warto przyjrzeć się swojej sytuacji spokojnie i konkretnie: co dokładnie zabiera najwięcej energii, co da się zmienić w najbliższych tygodniach, a co wymaga rozmowy z kimś z zewnątrz. Warto rozważyć konsultację ze specjalistą.",
    ],
  },
  {
    min: 61,
    max: 80,
    headline: "Twój organizm może bardzo wyraźnie mówić: «to jest za dużo»",
    body: [
      "Wynik wskazuje na częste występowanie objawów w większości obszarów: energii, stosunku do pracy, koncentracji i emocji. Osoby z takim obrazem często długo funkcjonują na rezerwie, bo odpowiedzialność wobec innych wydaje się ważniejsza od własnego stanu.",
      "Zdecydowanie warto rozważyć konsultację ze specjalistą: psychologiem, psychoterapeutą lub lekarzem. Jeśli pojawia się u Ciebie obniżony nastrój utrzymujący się tygodniami, nie odkładaj tej rozmowy.",
    ],
  },
];

export function rangeFor(total: number): BurnoutRange {
  return (
    burnoutRanges.find((range) => total >= range.min && total <= range.max) ?? burnoutRanges[0]!
  );
}

export const burnoutFaq = [
  {
    q: "Czym jest test na wypalenie zawodowe?",
    a: "To checklista, która pomaga uporządkować obserwacje dotyczące własnego stanu: energii, stosunku do pracy, koncentracji oraz emocji. Test edukacyjny pokazuje, jak często pojawiają się u Ciebie sygnały wypalenia zawodowego, ale nie rozstrzyga, czy wypalenie występuje.",
  },
  {
    q: "Jak rozpoznać wypalenie zawodowe?",
    a: "Światowa Organizacja Zdrowia opisuje w ICD-11 wypalenie zawodowe jako zjawisko zawodowe z trzema obszarami: wyczerpaniem, dystansem psychicznym wobec pracy oraz obniżoną skutecznością zawodową. Objawy wypalenia zawodowego zwykle narastają tygodniami i dotyczą kilku obszarów jednocześnie, a nie jednego trudnego tygodnia.",
  },
  {
    q: "Czy wynik testu to diagnoza?",
    a: "Nie. Żaden wynik tego testu nie jest diagnozą. Wypalenie zawodowe nie jest w ICD-11 klasyfikowane jako jednostka chorobowa, a ocena stanu zdrowia należy do specjalisty podczas konsultacji.",
  },
  {
    q: "Co zrobić z wysokim wynikiem?",
    a: "Warto przyjrzeć się swojej sytuacji: obciążeniu, granicom, możliwości oddania części zadań oraz temu, jak długo utrzymuje się dany stan. Przy utrzymujących się objawach warto rozważyć konsultację ze specjalistą.",
  },
  {
    q: "Ile trwa test?",
    a: "Około trzech minut. Test składa się z 20 pytań, a wynik pojawia się od razu po odpowiedzi na wszystkie z nich.",
  },
];

export const METHOD_NOTE =
  "Test ma charakter edukacyjny i przesiewowy. Nie jest narzędziem diagnostycznym i nie zastępuje konsultacji ze specjalistą.";

export const METHOD_LONG_NOTE =
  "Test jest autorską checklistą edukacyjną inspirowaną obszarami wypalenia opisywanymi w literaturze naukowej, m.in. w badaniach nad Burnout Assessment Tool (BAT). Nie jest oficjalną wersją BAT, MBI ani innego zwalidowanego kwestionariusza. Przedziały punktowe mają charakter edukacyjny i nie stanowią klinicznych punktów odcięcia.";
